<template>
  <v-container fluid>
    <FilterForm @filter="filter" :disabled="loading" />
    <v-row justify="center" align="center">
      <div :style="$vuetify.breakpoint.mobile ? 'width: 100%;' : 'width: 80%;'" class="mx-4 mb-4">
        <BulkBtn
          :items="selectedItems"
          v-if="selectedItems.length"
          @bulk-download="bulkDownloadRec" />
        <v-data-table
          ref="cdrDataTable"
          v-model="selectedItems"
          :loading="loading"
          :loading-text="$t('cdrs.loading')"
          :headers="headers"
          :no-data-text="$t('cdrs.tip.noData')"
          :options.sync="pagination"
          :server-items-length="totalRec"
          :items-per-page="defaultItemPerPage"
          :footer-props="footerProps"
          :disable-sort="true"
          :items="localList"
          show-select>

          <template v-slot:[`item.direction`]="{ item }">
            <Icon :rec="item" />
          </template>

          <template v-slot:[`item.from`]="{ item }">
            {{ item.fromDisplay }}
          </template>

          <template v-slot:[`item.to`]="{ item }">
            {{ item.toDisplay }}
          </template>

          <template v-slot:[`item.duration`]="{ item }">
            {{ item.duration | formatTime }}
          </template>

          <template v-slot:[`item.date`]="{ item }">
            {{ item.callTimestamp | formatDateLong($i18n.locale) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <Actions
              :has-rec="item.recording"
              :cdr="item"
              @download="downloadRec(item)"
              @playrec="playRec(item)"
              @callback="callBack(item)"
              @addToContacts="addToContacts"
              @addBlacklist="addBlacklist" />
          </template>
        </v-data-table>
      </div>
    </v-row>
    <Player :show="showPlayer" :clid="playerClid"
      :date="playerDate" :duration="playerDur"
      :src="recSrc" @player-close="showPlayer = false" />
    <ContactForm/>
    <BulkDownloadDialog
      :show="bulkDownloadDialogShow"
      :message="bulkDownloadMessage"
      @dialog-close="closeBulkDownloadDialog"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { urlsafeEncode } from '@/helpers/base64'
import { downloadBlob } from '@/helpers/download'
import API from '@/api'
import events from 'Store/events'
import Icon from './icon'
import Actions from './actions'
import FilterForm from './filter_form'
import Player from './player'
import Contact from 'Store/class/contact/contact'
import ContactForm from '@/components/contacts/forms/contact_dialog'
import BulkBtn from './bulk_btn'
import BulkDownloadDialog from './bulk_download_dialog'

export default {
  name: 'cdrs',
  data () {
    return {
      render: true,
      loading: false,
      recSrc: null,
      playerClid: null,
      playerDate: null,
      playerDur: null,
      showPlayer: false,
      pagination: {},
      defaultItemPerPage: 18,
      selectedItems: [],
      bulkDownloadDialogShow: false,
      bulkDownloadMessage: '',
      debounceTimeout: null
    }
  },
  mounted () {
    this.setPage(1)
  },
  watch: {
    pagination: {
      handler () {
        clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          this.setPage(this.pagination?.page)
          this.setPerPage(this.pagination?.itemsPerPage)
          this.load()
        }, 500)
      },
      deep: true
    }
  },
  beforeDestroy () {
    clearTimeout(this.debounceTimeout)
  },
  components: { Icon, Actions, FilterForm, Player, ContactForm, BulkBtn, BulkDownloadDialog },
  computed: {
    ...mapGetters({
      totalRec: 'cdrs/total',
      list: 'cdrs/list',
      perPage: 'cdrs/perPage',
      currentUser: 'currentUser/currentUser',
      phoneNumbers: 'contacts/phoneNumbers/phoneNumbers'
    }),
    headers () {
      return [
        { text: 'Direction', value: 'direction' },
        { text: this.$t('cdrs.columns.from'), value: 'from' },
        { text: this.$t('cdrs.columns.to'), value: 'to' },
        { text: this.$t('cdrs.columns.duration'), value: 'duration' },
        { text: 'Date', value: 'date' },
        { text: 'Actions', value: 'actions' }
      ]
    },
    footerProps () {
      return {
        'items-per-page-options': [5, 10, 15, 18, 20, 25, 50, 100],
        pageText: this.$t('cdrs.footerProps.pageText'),
        'items-per-page-text': this.$t('cdrs.footerProps.total')
      }
    },
    localList () {
      this.list.forEach(item => {
        if (!item.toName || item.toName.length === 0) {
          const contact = this.phoneNumbers.find((number) => { return number.phoneNumber.replace(/\D/g, '') === item.toNum.replace(/\D/g, '') })
          if (contact) {
            const temp = contact.name === 'feature_code' ? this.$t(`contacts.featureCodes.${contact.label}`) : contact.name
            this.updateCDR({ id: item.id, data: { toName: temp } })
          }
        }
      })
      return this.list
    }
  },
  methods: {
    ...mapActions({
      loadPage: 'cdrs/loadPage',
      setPage: 'cdrs/setPage',
      callNumber: 'callNumber',
      setPerPage: 'cdrs/setPerPage',
      openContactForm: 'contacts/form/open',
      updateCDR: 'cdrs/updateCDR',
      saveBlacklist: 'settings/saveBlacklist'
    }),
    load: function () {
      this.loading = true
      this.loadPage().catch(err => {
        this.dbg(err.response)
        events.pubError(this.$t('cdrs.err.load'))
      }).finally(() => {
        this.loading = false
      })
    },
    filter: function () {
      // reset to page 1 before filter
      this.setPage(1)
      this.load()
    },
    callBack: function (cdr) {
      const num = cdr.isInbound ? cdr.fromNum : cdr.toNum
      this.callNumber({ number: num })
    },
    playRec: function (rec) {
      this.getRecording(rec.id).then(resp => {
        if (!resp || !resp.data) return
        this.recSrc = URL.createObjectURL(resp.data)
        this.playerClid = rec.isInbound ? rec.fromDisplay : rec.toDisplay
        this.playerDate = new Date(rec.date).getTime()
        this.playerDur = rec.duration
        this.showPlayer = true
      })
    },
    downloadRec: function (rec) {
      this.getRecording(rec.id).then(resp => {
        if (!resp || !resp.data) return
        const filename = this.getRecordingFilename(rec, resp.data.type)
        downloadBlob(resp.data, filename)
      })
    },
    getRecordingFilename: function (rec, mimeType) {
      const date = new Date(rec.calldate).toISOString()
        .replace(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}).*/, '$1$2$3_$4$5$6')
      const ext = mimeType.split('/').pop()
      return `recording_${date}_from_${rec.fromNum}_to_${rec.toNum}_${rec.id}.${ext}`
    },
    getRecording: function (id) {
      events.mask('Loading recording...')
      return API.getCDRRec(id).catch(err => {
        this.dbg(err.response)
        events.pubError({ key: 'cdrs.err.recNotFound' })
      }).finally(() => {
        events.unmask()
      })
    },
    addToContacts: function ({ action, cdr }) {
      const isUpdate = false
      const contact = new Contact({})
      contact.isPersonal = false
      switch (action) {
        case 'add_personal_contact':
          contact.isPersonal = true
          break
      }
      contact.phoneNumbers[0].phone_number = this.currentUser.extension !== cdr.fromNum ? cdr.fromNum : cdr.toNum
      contact.lastName = this.currentUser.extension !== cdr.fromNum ? cdr.fromName : cdr.toName
      this.openContactForm({ contact, isUpdate })
    },
    addBlacklist: function (cdr) {
      const call = this.currentUser.extension !== cdr.fromNum ? { name: cdr.fromName, number: cdr.fromNum } : { name: (cdr.toName || cdr.calleeId), number: cdr.toNum }
      this.saveBlacklist(call)
    },
    bulkDownloadRec (items) {
      const encodedIds = urlsafeEncode(items.map(item => item.id).join(','))
      API.getBulkCDRRec(encodedIds).then(() => {
        this.bulkDownloadMessage = this.$t('cdrs.recordings_download.success')
      }).catch(() => {
        this.bulkDownloadMessage = this.$t('cdrs.recordings_download.error')
      }).finally(() => {
        this.bulkDownloadDialogShow = true
      })
    },
    closeBulkDownloadDialog () {
      this.bulkDownloadDialogShow = false
      this.selectedItems = []
    }
  }
}
</script>

<style scoped>
</style>
